// The example scripts: the tree that opens one (Category > script), a tab per
// open script, the Monaco model per script, and edits kept in storage until
// Revert. Like Monaco's own tabbed editors, one editor swaps between models
// and keeps each tab's view state (cursor, scroll, folding).

import { el, icon, ICONS } from './dom.js';

export function createLibrary( { els, store, scripts } ) {

	const models = new Map();
	const viewStates = new Map();
	const collapsed = new Set( JSON.parse( store.get( 'collapsed' ) ?? '[]' ) );
	let monaco = null;
	let editor = null;
	let active = scripts[ 0 ];
	let tabs = JSON.parse( store.get( 'tabs' ) ?? '[]' ).filter( ( id ) => byId( id ) );

	function byId( id ) {

		return scripts.find( ( item ) => item.id === id ) ?? null;

	}

	function isEdited( script ) {

		return store.get( `script.${ script.id }` ) != null;

	}

	// ---- Tree ----------------------------------------------------------------

	function matches( script, query ) {

		return ! query || `${ script.category } ${ script.title } ${ script.blurb }`.toLowerCase().includes( query );

	}

	function render() {

		const query = els.scriptsFilter.value.trim().toLowerCase();
		const groups = new Map();
		for ( const script of scripts ) {

			if ( ! matches( script, query ) ) continue;
			if ( ! groups.has( script.category ) ) groups.set( script.category, [] );
			groups.get( script.category ).push( script );

		}

		els.scriptsTree.textContent = '';
		for ( const [ category, list ] of groups ) {

			// A filter opens every group it matches.
			const open = query !== '' || ! collapsed.has( category );
			const items = el( 'div', { class: 'pg-tree-items', role: 'group', hidden: ! open } );
			for ( const script of list ) {

				items.append( el( 'button', {
					class: script === active ? 'pg-tree-item is-active' : 'pg-tree-item',
					type: 'button',
					role: 'treeitem',
					'data-id': script.id,
					'aria-selected': String( script === active ),
					title: script.blurb,
				}, [
					el( 'span', { text: script.title } ),
					isEdited( script ) ? el( 'span', { class: 'pg-tree-edited', title: 'Edited', text: '•' } ) : null,
				] ) );

			}

			const head = el( 'button', {
				class: 'pg-tree-group',
				type: 'button',
				role: 'treeitem',
				'data-category': category,
				'aria-expanded': String( open ),
			}, [
				icon( ICONS.chevron ),
				el( 'span', { text: category } ),
				el( 'span', { class: 'pg-tree-count', text: String( list.length ) } ),
			] );
			els.scriptsTree.append( el( 'div', { class: 'pg-tree-branch' }, [ head, items ] ) );

		}

		if ( ! groups.size ) els.scriptsTree.append( el( 'p', { class: 'pg-tree-empty', text: 'No script matches.' } ) );

	}

	function setOpen( open ) {

		els.scriptsPanel.hidden = ! open;
		els.scriptsButton.setAttribute( 'aria-expanded', String( open ) );
		els.scriptsButton.classList.toggle( 'is-active', open );
		if ( ! open ) return;
		render();
		const current = els.scriptsTree.querySelector( '.is-active' );
		current?.scrollIntoView( { block: 'nearest' } );
		( current ?? els.scriptsFilter ).focus();

	}

	els.scriptsButton.addEventListener( 'click', () => setOpen( els.scriptsPanel.hidden ) );
	els.scriptsClose.addEventListener( 'click', () => setOpen( false ) );
	els.scriptsFilter.addEventListener( 'input', render );

	els.scriptsTree.addEventListener( 'click', ( event ) => {

		const item = event.target.closest( '[data-id]' );
		if ( item ) {

			select( item.dataset.id );
			setOpen( false );
			return;

		}

		const group = event.target.closest( '[data-category]' );
		if ( ! group ) return;
		const { category } = group.dataset;
		if ( collapsed.has( category ) ) collapsed.delete( category );
		else collapsed.add( category );
		store.set( 'collapsed', JSON.stringify( [ ...collapsed ] ) );
		render();
		els.scriptsTree.querySelector( `[data-category="${ CSS.escape( category ) }"]` )?.focus();

	} );

	// Up and Down move between visible rows; Esc closes before it can stop a run.
	els.scriptsPanel.addEventListener( 'keydown', ( event ) => {

		if ( event.key === 'Escape' ) {

			event.stopPropagation();
			setOpen( false );
			els.scriptsButton.focus();
			return;

		}

		if ( event.key !== 'ArrowDown' && event.key !== 'ArrowUp' ) return;
		const rows = [ ...els.scriptsTree.querySelectorAll( '[role="treeitem"]' ) ].filter( ( row ) => row.offsetParent );
		if ( ! rows.length ) return;
		event.preventDefault();
		const at = rows.indexOf( document.activeElement );
		const next = event.key === 'ArrowDown' ? Math.min( rows.length - 1, at + 1 ) : Math.max( 0, at - 1 );
		rows[ at === - 1 ? 0 : next ].focus();

	} );

	document.addEventListener( 'pointerdown', ( event ) => {

		if ( els.scriptsPanel.hidden ) return;
		if ( els.scriptsPanel.contains( event.target ) || els.scriptsButton.contains( event.target ) ) return;
		setOpen( false );

	} );

	// ---- Editor --------------------------------------------------------------

	function modelFor( script ) {

		let model = models.get( script.id );
		if ( model ) return model;
		const code = store.get( `script.${ script.id }` ) ?? script.code;
		model = monaco.editor.createModel( code, 'javascript', monaco.Uri.parse( `file:///scripts/${ script.id }.js` ) );
		let timer = 0;
		model.onDidChangeContent( () => {

			clearTimeout( timer );
			timer = setTimeout( () => {

				const value = model.getValue();
				const edited = isEdited( script );
				store.set( `script.${ script.id }`, value === script.code ? null : value );
				if ( edited === isEdited( script ) ) return;
				renderTabs();
				if ( ! els.scriptsPanel.hidden ) render();

			}, 300 );

		} );
		models.set( script.id, model );
		return model;

	}

	// ---- Tabs ----------------------------------------------------------------

	function renderTabs() {

		els.tabs.textContent = '';
		for ( const id of tabs ) {

			const script = byId( id );
			const selected = script === active;
			const classes = [ 'pg-tab', selected ? 'is-active' : '', isEdited( script ) ? 'is-edited' : '' ].filter( Boolean );
			els.tabs.append( el( 'div', {
				class: classes.join( ' ' ),
				role: 'tab',
				tabindex: selected ? '0' : '-1',
				'data-id': id,
				'aria-selected': String( selected ),
				title: `${ script.title }: ${ script.blurb }`,
			}, [
				el( 'span', { class: 'pg-tab-name', text: `${ id }.js` } ),
				tabs.length > 1 ? el( 'button', { class: 'pg-tab-close', type: 'button', tabindex: '-1', 'aria-label': `Close ${ id }.js`, title: 'Close' }, [ icon( ICONS.close ) ] ) : null,
			] ) );

		}

		els.tabs.querySelector( '.is-active' )?.scrollIntoView( { block: 'nearest', inline: 'nearest' } );

	}

	function select( id ) {

		const next = byId( id ) ?? scripts[ 0 ];
		if ( editor && active && models.has( active.id ) ) viewStates.set( active.id, editor.saveViewState() );
		active = next;
		if ( ! tabs.includes( active.id ) ) tabs.push( active.id );
		store.set( 'tabs', JSON.stringify( tabs ) );
		store.set( 'last', active.id );
		if ( location.hash.slice( 1 ) !== active.id ) history.replaceState( null, '', `#${ active.id }` );
		renderTabs();
		if ( editor ) {

			editor.setModel( modelFor( active ) );
			const state = viewStates.get( active.id );
			if ( state ) editor.restoreViewState( state );
			editor.focus();

		}

	}

	function close( id ) {

		if ( tabs.length < 2 ) return;
		const at = tabs.indexOf( id );
		if ( at === - 1 ) return;
		tabs.splice( at, 1 );
		viewStates.delete( id );
		if ( active.id === id ) {

			active = null;
			select( tabs[ Math.min( at, tabs.length - 1 ) ] );

		} else {

			store.set( 'tabs', JSON.stringify( tabs ) );
			renderTabs();

		}

	}

	els.tabs.addEventListener( 'click', ( event ) => {

		const tab = event.target.closest( '[data-id]' );
		if ( ! tab ) return;
		if ( event.target.closest( '.pg-tab-close' ) ) close( tab.dataset.id );
		else if ( tab.dataset.id !== active.id ) select( tab.dataset.id );

	} );
	// Middle click closes, as in most editors.
	els.tabs.addEventListener( 'auxclick', ( event ) => {

		const tab = event.target.closest( '[data-id]' );
		if ( tab && event.button === 1 ) close( tab.dataset.id );

	} );
	els.tabs.addEventListener( 'keydown', ( event ) => {

		const at = tabs.indexOf( active.id );
		if ( event.key === 'ArrowRight' || event.key === 'ArrowLeft' ) {

			event.preventDefault();
			const next = tabs[ ( at + ( event.key === 'ArrowRight' ? 1 : tabs.length - 1 ) ) % tabs.length ];
			select( next );
			els.tabs.querySelector( '.is-active' )?.focus();

		} else if ( event.key === 'Delete' ) close( active.id );

	} );

	els.revert.addEventListener( 'click', () => {

		store.set( `script.${ active.id }`, null );
		models.get( active.id )?.setValue( active.code );
		renderTabs();
		if ( ! els.scriptsPanel.hidden ) render();

	} );
	window.addEventListener( 'hashchange', () => {

		const script = byId( location.hash.slice( 1 ) );
		if ( script && script !== active ) select( script.id );

	} );

	return {
		get active() {

			return active;

		},
		/** Reopen the last tabs, then pick the script from the URL hash, else the last one used. */
		start() {

			active = byId( location.hash.slice( 1 ) ) ?? byId( store.get( 'last' ) ) ?? scripts[ 0 ];
			select( active.id );

		},
		/** Hand the library the editor once Monaco is up. */
		attach( api ) {

			monaco = api.monaco;
			editor = api.editor;
			select( active.id );

		},
		code() {

			if ( editor ) return editor.getValue();
			return store.get( `script.${ active.id }` ) ?? active.code;

		},
	};

}
